import { lazy, type ComponentType } from 'react';

// Kod parçası (lazy chunk) yükleme hatalarına karşı dayanıklılık.
//
// NEDEN: Googlebot ana sayfayı, yüzen QuizBubble'ın chunk'ı (4244) yüklenemediği
// bir anda render etti; hata rota sınırına çıkıp TÜM sayfanın yerine React
// Router'ın varsayılan "Unexpected Application Error!" ekranını çizdi ve Google
// bunu başlık/özet olarak indeksledi. Chunk isteği; ağ kesintisi, tarayıcının
// kaynak isteğini düşürmesi ya da deploy anındaki eksik dosya yüzünden düşebilir.

const CHUNK_ERROR_PATTERNS = [
  /Loading chunk [\dA-Za-z-]+ failed/i,
  /Loading CSS chunk [\dA-Za-z-]+ failed/i,
  /ChunkLoadError/i,
  /Failed to fetch dynamically imported module/i,
  /Importing a module script failed/i,
];

export const isChunkLoadError = (error: unknown) => {
  const text =
    error instanceof Error
      ? `${error.name} ${error.message} ${error.stack ?? ''}`
      : String(error);
  return CHUNK_ERROR_PATTERNS.some((pattern) => pattern.test(text));
};

const RETRIES = 3;

// Başarısız chunk'ı artan beklemeyle yeniden ister. webpack, hata veren chunk'ın
// kaydını sıfırladığı için tekrar import() dosyayı gerçekten yeniden indirir.
// Chunk dışı hatalar (modül içi çalışma hatası) tekrar denemeyle düzelmez → hemen fırlatılır.
const retryImport = async <T>(factory: () => Promise<T>): Promise<T> => {
  for (let attempt = 0; ; attempt++) {
    try {
      return await factory();
    } catch (error) {
      if (attempt >= RETRIES || !isChunkLoadError(error)) throw error;
      await new Promise((resolve) => setTimeout(resolve, 500 * 2 ** attempt));
    }
  }
};

// React.lazy ile aynı imza; yalnızca import yeniden denenir.
export const lazyWithRetry = <T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>,
) => lazy(() => retryImport(factory));

// Tekrar denemeler de tükenirse son çare: sayfayı taze index.html ile BİR KEZ
// yeniden yükle (eski sürümün main.js'i silinmiş chunk'ı istiyor olabilir).
//
// Döngüyü engelleyen işaret URL'dedir, sessionStorage'da değil: Googlebot ve
// gizli pencereler depolamayı tutmayabilir. Açılışta işaret okunup adresten
// silinir → router temiz adresi görür, ?__reload'lı kopyalar indekslenmez.
// Bu modül createBrowserRouter'dan ÖNCE değerlendirilmelidir (bkz. index.tsx).
const RELOAD_PARAM = '__reload';

const bootUrl = new URL(window.location.href);
const reloadedAfterChunkError = bootUrl.searchParams.has(RELOAD_PARAM);
if (reloadedAfterChunkError) {
  bootUrl.searchParams.delete(RELOAD_PARAM);
  window.history.replaceState(window.history.state, '', bootUrl.toString());
}

export const shouldReloadForChunkError = (error: unknown) =>
  !reloadedAfterChunkError && isChunkLoadError(error);

export const reloadForChunkError = () => {
  const next = new URL(window.location.href);
  next.searchParams.set(RELOAD_PARAM, Date.now().toString(36));
  window.location.replace(next.toString());
};
